<script lang="ts">
import type { DisplayTemplateAst } from '#layers/data-model/types/display-template';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { CollectionFieldPath } from '#layers/data-model/utils/collection-field-path';
import type { InputProps, NestedSelectMenuItem, SuggestionGroup, SuggestionItem } from '#layers/ui/public';
import nuxtUiInputTheme from '#build/ui/input';
import { buildCollectionFieldPathItems, resolveCollectionFieldPathSelection, searchCollectionFieldPathEntries } from '#layers/data-model/utils/collection-field-path';

export interface DisplayTemplateInputProps {
	collection?: ResolvedMonospaceCollection;
	defaultValue?: DisplayTemplateAst | null;
	size?: InputProps['size'];
	disabled?: boolean;
	readonly?: boolean;
}

interface DisplayTemplateFieldSuggestionItem extends SuggestionItem {
	fieldPath: CollectionFieldPath;
	leafLabel: string;
	leafApiName: string;
	ancestorLabels: string[];
	displayPathLabels: string[];
	apiPathLabels: string[];
}

interface DisplayTemplateFieldPathMenuItem extends NestedSelectMenuItem {
	apiName: string;
	displayName: string;
}
</script>

<script setup lang="ts">
import { Text } from '@tiptap/extension-text';
import { Dropcursor, Gapcursor, UndoRedo } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/vue-3';
import { tv } from 'tailwind-variants';
import { computed, ref, watch } from 'vue';
import { useAppConfig } from '#app/config';
import CollectionFieldPathSelect from '#layers/data-model/components/Collection/CollectionFieldPathSelect.vue';
import { useDataModelDisplayNames } from '#layers/data-model/composables/use-data-model-display-names';
import { fieldPathToDisplayTemplateRefNode } from '#layers/data-model/utils/display-template';
import { DisplayTemplateFieldRef } from '#layers/data-model/utils/extensions/display-template-field-ref';
import { flattenWithPaths, InlineDocument, prioritizeTopLevelMatches, SuggestionMenu, useFormField, useMergedTheme } from '#layers/ui/public';

const props = defineProps<DisplayTemplateInputProps>();

const displayTemplate = defineModel<DisplayTemplateAst | null>();

const appConfig = useAppConfig();
const inputTheme = useMergedTheme({ input: nuxtUiInputTheme });

const { emitFormBlur, emitFormFocus, emitFormInput, emitFormChange, size: formSize, ariaAttrs } = useFormField<DisplayTemplateInputProps>(props as any, { deferInputValidation: true });

const theme = computed(() => tv(inputTheme)({ size: formSize.value }));
const { getFieldDisplayName } = useDataModelDisplayNames();
const isDisabled = computed(() => props.disabled ?? false);
const isReadOnlyMode = computed(() => props.readonly ?? false);
const isEditable = computed(() => !isDisabled.value && !isReadOnlyMode.value);
const SUGGESTION_SEARCH_DEPTH = 3;

const fieldPathValidationOptions = {
	includeConstrainedFields: false,
	includeListRelationFields: false,
	terminal: 'primitive-or-single-relation' as const,
};

const fieldPathBuildOptions = {
	...fieldPathValidationOptions,
	label: 'displayName' as const,
	getFieldDisplayName,
};

function omitNullRawFieldIds(value: unknown): unknown {
	if (Array.isArray(value)) {
		return value.map((item) => omitNullRawFieldIds(item));
	}

	if (!value || typeof value !== 'object') {
		return value;
	}

	return Object.fromEntries(
		Object.entries(value)
			.filter(([key, entryValue]) => key !== 'rawFieldId' || entryValue != null)
			.map(([key, entryValue]) => [key, omitNullRawFieldIds(entryValue)]),
	);
}

const editor = useEditor({
	extensions: [
		// Nodes
		InlineDocument,
		Text,
		DisplayTemplateFieldRef,
		// Extensions
		Dropcursor,
		Gapcursor,
		UndoRedo,
	],
	editorProps: {
		attributes: {
			class: theme.value.base({ class: 'overflow-x-auto overflow-y-hidden [>*]:inline !break-keep !wrap-normal !text-nowrap scroll-pe-9 pe-9 scrollbar-hidden' }),
		},
	},
	content: displayTemplate.value ?? props.defaultValue,
	editable: isEditable.value,
	onUpdate: ({ editor }) => {
		displayTemplate.value = omitNullRawFieldIds(editor.getJSON()) as DisplayTemplateAst | null;

		emitFormChange();
		emitFormInput();
	},
	onBlur: () => {
		emitFormBlur();
	},
	onFocus: () => {
		emitFormFocus();
	},
});

watch(isEditable, (editable) => {
	editor.value?.setEditable(editable);
});

const fieldPathItems = computed(() => {
	if (!props.collection) return [];
	return buildCollectionFieldPathItems(props.collection, fieldPathBuildOptions);
});

function insertFieldPath(fieldPath: CollectionFieldPath) {
	if (!editor.value) return;
	if (!isEditable.value) return;

	editor.value
		.chain()
		.focus()
		.insertContent([fieldPathToDisplayTemplateRefNode(fieldPath)])
		.run();
}

const suggestionGroups = computed<SuggestionGroup<DisplayTemplateFieldSuggestionItem>[]>(() => {
	if (!props.collection) return [];

	const items: DisplayTemplateFieldSuggestionItem[] = [];

	for (const item of flattenWithPaths(fieldPathItems.value, SUGGESTION_SEARCH_DEPTH)) {
		const fieldPath = resolveCollectionFieldPathSelection(props.collection, item.pathIds, fieldPathValidationOptions);

		if (!fieldPath) continue;

		const path = item.path as DisplayTemplateFieldPathMenuItem[];
		const leafItem = item.item as DisplayTemplateFieldPathMenuItem;

		items.push({
			id: item.pathIds.join('.'),
			label: item.pathLabels.at(-1) ?? item.item.label,
			leafLabel: item.pathLabels.at(-1) ?? item.item.label,
			leafApiName: leafItem.apiName,
			ancestorLabels: item.pathLabels.slice(0, -1),
			displayPathLabels: path.map((segment) => segment.displayName),
			apiPathLabels: path.map((segment) => segment.apiName),
			fieldPath,
			ui: {
				itemLabel: 'text-default',
			},
			onSelect: () => {
				insertFieldPath(fieldPath);
			},
		});
	}

	return [{
		id: 'fields',
		items,
		postFilter: (searchTerm, filteredItems) =>
			searchTerm
				? prioritizeTopLevelMatches(filteredItems, (item) => item.ancestorLabels.length === 0)
				: filteredItems.filter((item) => item.ancestorLabels.length === 0),
	}];
});

const fieldSelectModel = ref<CollectionFieldPath | null>(null);

function searchSuggestionItems(query: string, items: (DisplayTemplateFieldSuggestionItem & { group: string })[]) {
	return searchCollectionFieldPathEntries(items, query, (item) => ({
		displayPathLabels: item.displayPathLabels,
		apiPathLabels: item.apiPathLabels,
		leafDisplayLabel: item.leafLabel,
		leafApiName: item.leafApiName,
		depth: item.ancestorLabels.length + 1,
	}));
}
</script>

<template>
	<div class="relative inline-flex w-full overflow-hidden" :class="{ 'opacity-50': isDisabled }">
		<EditorContent
			:editor
			:class="theme.root({ class: ['flex-1 w-full', isDisabled ? 'cursor-not-allowed' : isReadOnlyMode ? 'cursor-default' : undefined] })"
			:aria-disabled="isDisabled || undefined"
			:aria-readonly="isReadOnlyMode || undefined"
			v-bind="ariaAttrs"
		/>
		<SuggestionMenu
			v-if="isEditable"
			:editor
			:groups="suggestionGroups"
			:search-items="searchSuggestionItems"
			char="/"
			allow-spaces
			:allow-leading-space="false"
		>
			<template #item-label="{ item }">
				<span class="inline-flex items-center gap-1">
					<span
						v-for="(label, index) in item.ancestorLabels"
						:key="`${label}-${index}`"
						class="inline-flex items-center gap-1 text-muted"
					>
						<span>{{ label }}</span>
						<DIcon name="lucide:chevron-right" class="size-3" />
					</span>
					<span>{{ item.leafLabel }}</span>
				</span>
			</template>
		</SuggestionMenu>
		<CollectionFieldPathSelect
			v-if="collection && isEditable"
			v-model="fieldSelectModel"
			:collection
			:include-constrained-fields="false"
			:include-list-relation-fields="false"
			label="displayName"
			terminal="primitive-or-single-relation"
			:max-search-depth="SUGGESTION_SEARCH_DEPTH"
			:search-placeholder="$t('common.search')"
			:size
			@select="insertFieldPath"
		>
			<template #default="{ open }">
				<DButton
					variant="link"
					color="neutral"
					class="absolute inset-y-0 inset-e-0 w-10 justify-center"
					size="sm"
					:aria-label="$t('common.add_field')"
					:icon="appConfig.ui.icons.plus"
					:active="open"
				/>
			</template>
		</CollectionFieldPathSelect>
	</div>
</template>
